import React from 'react';
import dayjs from 'dayjs';
import { i18n, dateTokens } from '@lib/i18n';
import { t, Trans } from '@lingui/macro';
import { Button, ConfirmationDialog, Dialog, Typography } from '@oca/ui';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import {
  TextFieldWrapper,
  DayPickerInputWrapper,
  DATE_FIELD_SUBSCRIPTION,
} from '@lib/final-form-oca-ui';
import { Box } from 'rebass';
import { useStore } from 'effector-react';
import { $leaveAdjustFetching, adjustLeaveRequesting } from '../model/profile';
import { formatDays } from '../lib/helpers/leave-days';

// Nonzero, at most one decimal place, negative to subtract days.
const ADJUSTMENT_PATTERN = /^-?\d+(\.\d)?$/;

// eslint-disable-next-line react/prop-types
export const LeaveAdjustmentForm = ({ leave, user, onDialogClose }) => {
  const submitting = useStore($leaveAdjustFetching);
  const usedDays = formatDays(leave.amountUsedDays) || '0';
  const entitlementDays = formatDays(leave.leaveBalance) || '—';
  const availableDays = formatDays(leave.availableDays) || '—';
  const handleSubmitForm = async values => {
    try {
      await adjustLeaveRequesting({
        user: user.id,
        leaveType: leave.id,
        adjustment: Number(values.adjustment),
        effectiveDate: values.effectiveDate,
        reason: values.reason.trim(),
      });
      return null;
    } catch (error) {
      return toSubmitErrors(error);
    }
  };

  return (
    <Dialog
      onClickOnClose={onDialogClose}
      title={i18n._(t`Adjust leave`)}
      minWidth={400}
      underlayClickExits={false}
    >
      <Form
        onSubmit={handleSubmitForm}
        initialValues={{ effectiveDate: dayjs().format(dateTokens.ISO_DATE) }}
        validate={validate}
        render={({ handleSubmit, form, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Box mb={4}>
              <Typography variant="h3">{user.fullName}</Typography>
              <Typography variant="h6" color="textSecondary" fontSize={14}>
                {user.department.name} ({user.jobTitle})
              </Typography>
            </Box>
            <Box mb={3}>
              <Typography variant="h3" gutterBottom>
                <Trans>Type</Trans>: {leave.name}
              </Typography>
              <Typography>
                <Trans>Days used: {usedDays}</Trans>
              </Typography>
              <Typography>
                <Trans>Entitlement (days): {entitlementDays}</Trans>
              </Typography>
              <Typography>
                <Trans>Available (days): {availableDays}</Trans>
              </Typography>
            </Box>
            <Box mb={4}>
              <Field
                name="adjustment"
                component={TextFieldWrapper}
                inputMode="decimal"
                label={i18n._(t`Adjust value`)}
                helperText={i18n._(
                  t`Days to add, or negative to subtract (e.g. 2.5 or -1)`,
                )}
                required
                spaceAfter
              />
              <Field
                name="effectiveDate"
                label={i18n._(t`Effective date`)}
                helperText={i18n._(t`Selects the leave period to correct`)}
                component={DayPickerInputWrapper}
                subscription={DATE_FIELD_SUBSCRIPTION}
                parse={value =>
                  value ? dayjs(value).format(dateTokens.ISO_DATE) : ''
                }
                format={value => value || null}
                required
                spaceAfter
              />
              <Field
                name="reason"
                component={TextFieldWrapper}
                label={i18n._(t`Reason`)}
                placeholder={i18n._(t`Enter reason for adjustment`)}
                fullWidth
                spaceAfter
                required
              />
            </Box>
            {submitError && (
              <Box mb={3}>
                <Typography color="error" fontSize={13}>
                  {submitError}
                </Typography>
              </Box>
            )}
            <ConfirmationDialog
              title={<Trans>Leave adjustment</Trans>}
              description={
                <Trans>Are you sure you want to change this leave?</Trans>
              }
              confirmText={<Trans>Yes, I am sure</Trans>}
              cancelText={<Trans>Cancel</Trans>}
            >
              {confirm => (
                <Button
                  type="button"
                  variant="primary"
                  onClick={confirm(form.submit)}
                  disabled={submitting.isLoading}
                >
                  <Trans>Save</Trans>
                </Button>
              )}
            </ConfirmationDialog>
          </form>
        )}
      />
    </Dialog>
  );
};

// The server validates the resulting balance; only the input format is checked here.
function validate(values) {
  const errors = {};
  const adjustment = String(values.adjustment || '').trim();

  if (!adjustment) {
    errors.adjustment = i18n._(t`Value is required`);
  } else if (!ADJUSTMENT_PATTERN.test(adjustment)) {
    errors.adjustment = i18n._(t`Use a number with at most one decimal place`);
  } else if (Number(adjustment) === 0) {
    errors.adjustment = i18n._(t`Adjustment can't be zero`);
  }

  if (!values.effectiveDate) {
    errors.effectiveDate = i18n._(t`Effective date is required`);
  }

  if (!values.reason || !values.reason.trim()) {
    errors.reason = i18n._(t`You need to specify the reason`);
  }
  return errors;
}

const FIELDS = ['adjustment', 'effectiveDate', 'reason'];

function toSubmitErrors(error) {
  const data = (error && error.data) || {};
  const errors = {};

  if (typeof data === 'object') {
    FIELDS.forEach(name => {
      if (data[name]) errors[name] = data[name];
    });
  }

  // user / leave_type errors (e.g. statutory types reject adjustments) and
  // general errors have no field of their own here.
  const formMessage =
    (typeof data === 'string' && data) ||
    data.detail ||
    data.nonFieldErrors ||
    data.leaveType ||
    data.user ||
    (error && error.status === 403
      ? i18n._(t`You do not have permission to adjust leave balances.`)
      : null);

  if (formMessage) {
    errors[FORM_ERROR] = formMessage;
  } else if (!Object.keys(errors).length) {
    errors[FORM_ERROR] = i18n._(t`Something went wrong. Please try again.`);
  }

  return errors;
}
